import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { BrioModal, BrioModalBody, BrioButton } from '../../components/BrioComponents';
import { useRangeSelection } from '../hooks/useRangeSelection';

const SPEC_FIELDS = [
  { key: 'shortDescription', label: 'Short description', input: true },
  { key: 'unit', label: 'Unit', input: true },
  { key: 'measurementRef', label: 'Measurement ref', input: true },
  { key: 'longSpecGeneral', label: 'Spec — general' },
  { key: 'longSpecProducts', label: 'Spec — products' },
  { key: 'longSpecExecution', label: 'Spec — execution' },
];

const statusBadge = (status) => {
  if (status === 'draft') return { label: 'candidate', bg: 'rgba(176,131,0,0.15)', fg: '#8a6d00' };
  if (status === 'active') return { label: 'active', bg: 'rgba(46,125,50,0.13)', fg: '#2e7d32' };
  return { label: status || '—', bg: '#eee', fg: 'var(--brio-text-muted)' };
};

const specFrom = (v) => ({
  shortDescription: v.shortDescription || '',
  unit: v.unit || '',
  measurementRef: v.measurementRef || '',
  longSpecGeneral: v.longSpecGeneral || '',
  longSpecProducts: v.longSpecProducts || '',
  longSpecExecution: v.longSpecExecution || '',
  changeReason: '',
});

// Side-by-side reconciliation for one item: compare every version (active +
// candidate drafts), compose a canonical version, and fold the duplicates in
// (their rates re-point to the canonical; the variants are deprecated).
function ReconcileItemModal({ itemCode, versions, onReconcile, onClose, onDone }) {
  const itemVersions = useMemo(
    () => versions.filter((v) => v.itemCode === itemCode).sort((a, b) => a.fullCode.localeCompare(b.fullCode)),
    [versions, itemCode]
  );
  const drafts = useMemo(() => itemVersions.filter((v) => v.status === 'draft'), [itemVersions]);

  const [canonical, setCanonical] = useState(() => {
    const seed = itemVersions.find((v) => v.status === 'active') || drafts[0] || itemVersions[0];
    return seed ? specFrom(seed) : { shortDescription: '', unit: '', measurementRef: '', longSpecGeneral: '', longSpecProducts: '', longSpecExecution: '', changeReason: '' };
  });
  const { selected: fold, isSelected, toggle: toggleFold } = useRangeSelection(
    itemVersions.map((v) => v.id),
    drafts.map((v) => v.id),
  );
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = itemVersions.map((v) => v.id);
      if (!ids.length) return;
      const { data, error: e } = await supabase.schema('boq').from('rates').select('item_version_id').in('item_version_id', ids);
      if (!e && !cancelled) {
        const tally = {};
        (data || []).forEach((r) => { tally[r.item_version_id] = (tally[r.item_version_id] || 0) + 1; });
        setCounts(tally);
      }
    })();
    return () => { cancelled = true; };
  }, [itemVersions]);

  const copyInto = (v) => setCanonical((c) => ({ ...specFrom(v), changeReason: c.changeReason }));
  const setField = (k, val) => setCanonical((c) => ({ ...c, [k]: val }));

  const movedRates = useMemo(() => [...fold].reduce((sum, id) => sum + (counts[id] || 0), 0), [fold, counts]);

  const handleConfirm = async () => {
    setBusy(true); setError(null);
    const res = await onReconcile({ itemCode, canonical, foldVersionIds: [...fold] });
    if (!res.success) { setError(res.error); setBusy(false); return; }
    onDone?.();
    onClose();
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleConfirm} disabled={busy}>
        {busy ? 'Reconciling…' : `Create canonical · fold ${fold.size} (${movedRates} rate${movedRates === 1 ? '' : 's'})`}
      </BrioButton>
    </>
  );

  const inputStyle = { width: '100%', padding: '0.35rem 0.5rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, fontSize: '0.82rem', boxSizing: 'border-box' };
  const cardField = (label, value) => (
    <div style={{ marginBottom: '0.4rem' }}>
      <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)' }}>{label}</div>
      <div style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', maxHeight: 90, overflow: 'auto' }}>{value || <span style={{ color: 'var(--brio-text-muted)' }}>—</span>}</div>
    </div>
  );

  return (
    <BrioModal title={`Reconcile ${itemCode}`} onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {error && <div style={{ padding: '0.6rem 0.9rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '0.9rem' }}>{error}</div>}
        <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
          Compare the variants, compose the canonical version below, and tick the ones to fold in. Folded variants are
          deprecated and their rate observations re-point to the new canonical version (provenance is preserved).
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {itemVersions.map((v) => {
            const b = statusBadge(v.status);
            return (
              <div key={v.id} style={{ border: isSelected(v.id) ? '2px solid var(--brio-gold, #9F902A)' : '1px solid var(--brio-border, #ccc)', borderRadius: 6, padding: '0.6rem', background: v.status === 'deprecated' ? '#fafafa' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', gap: '0.4rem' }}>
                  <code style={{ fontSize: '0.78rem', fontWeight: 600 }}>{v.fullCode}</code>
                  <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', padding: '0 5px', borderRadius: 3, background: b.bg, color: b.fg }}>{b.label}</span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)', marginBottom: '0.4rem' }}>
                  {counts[v.id] || 0} rate{(counts[v.id] || 0) === 1 ? '' : 's'}
                  {v.changeReason ? ` · ${v.changeReason}` : ''}
                </div>
                {cardField('Description', v.shortDescription)}
                {cardField('Unit', v.unit)}
                {cardField('General', v.longSpecGeneral)}
                {cardField('Products', v.longSpecProducts)}
                {cardField('Execution', v.longSpecExecution)}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.4rem', gap: '0.4rem' }}>
                  <button type="button" onClick={() => copyInto(v)} style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer' }}>
                    Copy into canonical
                  </button>
                  <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={isSelected(v.id)} onChange={() => {}} onClick={(e) => toggleFold(v.id, e)} /> fold in
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ border: '1px solid var(--brio-gold, #9F902A)', borderRadius: 6, padding: '0.85rem', background: '#fffdf5' }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.6rem' }}>Canonical version</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem' }}>
            {SPEC_FIELDS.map((f) => (
              <label key={f.key} style={{ display: 'block', fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}>
                {f.label}{(f.key === 'shortDescription' || f.key === 'unit') ? ' *' : ''}
                {f.input
                  ? <input value={canonical[f.key]} onChange={(e) => setField(f.key, e.target.value)} style={inputStyle} />
                  : <textarea value={canonical[f.key]} onChange={(e) => setField(f.key, e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />}
              </label>
            ))}
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}>
              Change reason
              <input value={canonical.changeReason} onChange={(e) => setField('changeReason', e.target.value)} placeholder="Reconciled canonical version" style={inputStyle} />
            </label>
          </div>
        </div>
      </BrioModalBody>
    </BrioModal>
  );
}

export default ReconcileItemModal;
